import { formatInTimeZone, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { ro } from 'date-fns/locale';
import { 
  parseISO,
  format,
  addDays, 
  addHours, 
  addMinutes, 
  subDays, 
  subHours, 
  subMinutes, 
  differenceInDays, 
  differenceInHours, 
  differenceInMinutes, 
  isBefore, 
  isAfter, 
  startOfDay as startOfDayFns, 
  endOfDay as endOfDayFns 
} from 'date-fns';

// Timezone Romania
export const ROMANIA_TIMEZONE = 'Europe/Bucharest';

/**
 * Converteste un Date sau string intr-un Date in timezone-ul Romaniei
 */
export function toRomaniaTime(date: Date | string): Date {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return utcToZonedTime(dateObj, ROMANIA_TIMEZONE);
}

/**
 * Converteste un Date din timezone-ul Romaniei in UTC
 */
export function fromRomaniaTime(date: Date | string): Date {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return zonedTimeToUtc(dateObj, ROMANIA_TIMEZONE);
}

/**
 * Formateaza o data in timezone-ul Romaniei
 */
export function formatRomaniaTime(
  date: Date | string,
  formatStr: string = 'dd MMMM yyyy, HH:mm'
): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return formatInTimeZone(dateObj, ROMANIA_TIMEZONE, formatStr, { locale: ro });
}

/**
 * Obtine data si ora curenta in timezone-ul Romaniei
 */
export function nowInRomania(): Date {
  return utcToZonedTime(new Date(), ROMANIA_TIMEZONE);
}

/**
 * Adauga zile la o data in timezone-ul Romaniei
 */
export function addDaysRomania(date: Date | string, days: number): Date {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  const zonedDate = utcToZonedTime(dateObj, ROMANIA_TIMEZONE);
  return addDays(zonedDate, days);
}

/**
 * Adauga ore la o data in timezone-ul Romaniei
 */
export function addHoursRomania(date: Date | string, hours: number): Date {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  const zonedDate = utcToZonedTime(dateObj, ROMANIA_TIMEZONE);
  return addHours(zonedDate, hours);
}

/**
 * Adauga minute la o data in timezone-ul Romaniei
 */
export function addMinutesRomania(date: Date | string, minutes: number): Date {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return addMinutes(dateObj, minutes);
}

/**
 * Scade zile dintr-o data in timezone-ul Romaniei
 */
export function subDaysRomania(date: Date | string, days: number): Date {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return subDays(dateObj, days);
}

/**
 * Scade ore dintr-o data in timezone-ul Romaniei
 */
export function subHoursRomania(date: Date | string, hours: number): Date {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return subHours(dateObj, hours);
}

/**
 * Scade minute dintr-o data in timezone-ul Romaniei
 */
export function subMinutesRomania(date: Date | string, minutes: number): Date {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return subMinutes(dateObj, minutes);
}

/**
 * Calculeaza diferenta in zile intre doua date in timezone-ul Romaniei
 */
export function diffDaysRomania(dateLeft: Date | string, dateRight: Date | string): number {
  const leftObj = typeof dateLeft === 'string' ? parseISO(dateLeft) : dateLeft;
  const rightObj = typeof dateRight === 'string' ? parseISO(dateRight) : dateRight;
  return differenceInDays(leftObj, rightObj);
}

/**
 * Calculeaza diferenta in ore intre doua date in timezone-ul Romaniei
 */
export function diffHoursRomania(dateLeft: Date | string, dateRight: Date | string): number {
  const leftObj = typeof dateLeft === 'string' ? parseISO(dateLeft) : dateLeft;
  const rightObj = typeof dateRight === 'string' ? parseISO(dateRight) : dateRight;
  return differenceInHours(leftObj, rightObj);
}

/**
 * Calculeaza diferenta in minute intre doua date in timezone-ul Romaniei
 */
export function diffMinutesRomania(dateLeft: Date | string, dateRight: Date | string): number {
  const leftObj = typeof dateLeft === 'string' ? parseISO(dateLeft) : dateLeft;
  const rightObj = typeof dateRight === 'string' ? parseISO(dateRight) : dateRight;
  return differenceInMinutes(leftObj, rightObj);
}

/**
 * Verifica daca o data este inainte de alta in timezone-ul Romaniei
 */
export function isBeforeRomania(date: Date | string, dateToCompare: Date | string): boolean {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  const compareObj = typeof dateToCompare === 'string' ? parseISO(dateToCompare) : dateToCompare;
  const zonedDate = utcToZonedTime(dateObj, ROMANIA_TIMEZONE);
  const zonedCompare = utcToZonedTime(compareObj, ROMANIA_TIMEZONE);
  return isBefore(zonedDate, zonedCompare);
}

/**
 * Verifica daca o data este dupa alta in timezone-ul Romaniei
 */
export function isAfterRomania(date: Date | string, dateToCompare: Date | string): boolean {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  const compareObj = typeof dateToCompare === 'string' ? parseISO(dateToCompare) : dateToCompare;
  const zonedDate = utcToZonedTime(dateObj, ROMANIA_TIMEZONE);
  const zonedCompare = utcToZonedTime(compareObj, ROMANIA_TIMEZONE);
  return isAfter(zonedDate, zonedCompare);
}

/**
 * Obtine inceputul zilei in timezone-ul Romaniei
 */
export function startOfDayRomania(date: Date | string): Date {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  const zonedDate = utcToZonedTime(dateObj, ROMANIA_TIMEZONE);
  return startOfDayFns(zonedDate);
}

/**
 * Obtine sfarsitul zilei in timezone-ul Romaniei
 */
export function endOfDayRomania(date: Date | string): Date {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  const zonedDate = utcToZonedTime(dateObj, ROMANIA_TIMEZONE);
  return endOfDayFns(zonedDate);
}

/**
 * Formeaza un string pentru input datetime-local
 */
export function toDateTimeLocalString(date: Date | string): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  const zonedDate = utcToZonedTime(dateObj, ROMANIA_TIMEZONE);
  return format(zonedDate, "yyyy-MM-dd'T'HH:mm");
}

/**
 * Parseaza un string de la input datetime-local
 */
export function fromDateTimeLocalString(dateString: string): Date {
  const dateObj = parseISO(dateString);
  return zonedTimeToUtc(dateObj, ROMANIA_TIMEZONE);
}

